// Contract/wallet errors -> plain sentences that say what happened and what to do next.
import { BaseError, ContractFunctionRevertedError, InsufficientFundsError, UserRejectedRequestError } from 'viem';
import { ConnectorChainMismatchError } from '@wagmi/core';
import { chain } from '../config/chain';
import { formatDateTime, formatEth, shortAddress } from '../lib/format';
import { P } from '../lib/params';

export interface ErrorContext {
  kind?: 'buy' | 'sell' | 'create' | 'claim' | 'swap';
  /** challenge the proof was mined for */
  challengeId?: bigint;
  /** who solved a challenge, if known from the feed */
  solverOf?: (challengeId: bigint) => string | undefined;
}

export interface DecodedRevert {
  name: string;
  args: readonly unknown[];
}

export function decodeRevert(e: unknown): DecodedRevert | null {
  if (!(e instanceof BaseError)) return null;
  const r = e.walk((x) => x instanceof ContractFunctionRevertedError);
  if (r instanceof ContractFunctionRevertedError && r.data?.errorName) {
    return { name: r.data.errorName, args: (r.data.args ?? []) as readonly unknown[] };
  }
  return null;
}

const big = (x: unknown) => (typeof x === 'bigint' ? x : 0n);

export function explainError(e: unknown, ctx: ErrorContext = {}): string {
  const revert = decodeRevert(e);
  if (revert) return explainRevert(revert, ctx);
  if (e instanceof BaseError) {
    if (e.walk((x) => x instanceof UserRejectedRequestError)) return 'You rejected the request in your wallet. Nothing was sent.';
    if (e.walk((x) => x instanceof InsufficientFundsError)) return 'Your wallet does not have enough ETH for this transaction and its gas.';
    return e.shortMessage;
  }
  if (e instanceof ConnectorChainMismatchError) return `Your wallet is on another network. Switch it to ${chain.name}.`;
  if (e instanceof Error) {
    if (/user rejected|denied/i.test(e.message)) return 'You rejected the request in your wallet. Nothing was sent.';
    return e.message;
  }
  return String(e);
}

function explainRevert({ name, args }: DecodedRevert, ctx: ErrorContext): string {
  switch (name) {
    case 'StaleChallenge': {
      const current = big(args[0]);
      const mined = ctx.challengeId ?? current - 1n;
      const solver = ctx.solverOf?.(mined);
      return `Challenge #${mined} was solved by ${solver ? shortAddress(solver) : 'another miner'} first. Your miner has switched to #${current}.`;
    }
    case 'InsufficientWork':
      return 'This proof does not meet the target at the next block yet. Difficulty keeps easing while the challenge is idle, so try again in a few seconds.';
    case 'SlippageExceeded':
      if (ctx.kind === 'sell') {
        return `The sale would now return ${formatEth(big(args[1]))}, below your minimum of ${formatEth(big(args[0]))}. Raise the slippage limit or try again.`;
      }
      return `The lot now costs ${formatEth(big(args[0]))}, above your maximum of ${formatEth(big(args[1]))}. Raise the slippage limit or try again.`;
    case 'OneForgePerSecond':
      return 'A lot was just forged: the contract accepts at most one proof per block and per second. Wait a moment and try again.';
    case 'AlreadyIgnited':
      return 'Someone already opened the forge. Challenge #1 is revealed in the next block.';
    case 'ChallengeNotRevealed':
      return `Challenge #${ctx.challengeId ?? ''} is revealed next block; your miner will switch to it automatically.`;
    case 'InvalidCreator':
      return 'That address cannot receive the creator role (the launch, token or pool contract).';
    case 'OnlyPendingCreator':
      return 'Only the address the creator nominated can accept the creator role.';
    case 'NotActive':
      return 'This launch has graduated, so the curve is closed. Trade in the Uniswap pool instead.';
    case 'NotOpenYet':
      return `The launch opens at ${formatDateTime(Number(big(args[0])))} (chain time). Try again then.`;
    case 'Expired':
      return 'The deadline passed before the transaction was included. Try again.';
    case 'InsufficientPayment':
      return `The payment sent (${formatEth(big(args[1]))}) was below the lot cost (${formatEth(big(args[0]))}). Try again.`;
    case 'InvalidAmount':
      return 'Enter an amount above zero and no larger than the tokens in circulation.';
    case 'NotGraduated':
      return 'LP fees can be collected only after graduation.';
    case 'BadName':
      return 'The name must be 1 to 32 bytes.';
    case 'BadSymbol':
      return 'The symbol must be 1 to 12 letters or digits.';
    case 'BadMetadata':
      return 'The metadata link is too long (512 bytes at most).';
    case 'BadOpenAt':
      return `The start must be between now and ${P.maxStartDays} days from now, in chain time.`;
    case 'CreationPaused':
      return 'Launch creation is paused right now. Existing launches are not affected.';
    case 'WrongLaunchFee':
      return `The launch fee is ${formatEth(big(args[0]))}. Reload the page and try again.`;
    case 'InsufficientBalance':
      return 'Your token balance is too low for this amount.';
    case 'InsufficientAllowance':
      return 'The router is not approved to spend this amount yet. Approve first.';
    case 'PoolLockedUntilGraduation':
      return 'The pool opens at graduation. Until then, sell back to the curve.';
    default:
      return `The contract rejected the transaction (${name}).`;
  }
}
